import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, clearToken, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Check, Upload, Copy, Award, UserPlus, FileSpreadsheet, Edit3, Archive, ArchiveRestore } from "lucide-react";
import AdminStats from "@/components/site/AdminStats";
import { CefLogoImg } from "@/components/site/BrandLogo";
import EditModal from "@/components/site/EditModal";
import RichTextEditor from "@/components/site/RichTextEditor";
import ImageUpload from "@/components/site/ImageUpload";
import FAQAdminTab from "@/pages/admin/FAQAdminTab";
import ContenusTab from "@/pages/admin/ContenusTab";
import PageBlocksTab from "@/pages/admin/PageBlocksTab";
import MembresTab from "@/pages/admin/MembresTab";
import SiteConfigTab from "@/pages/admin/SiteConfigTab";
import LocalGroupsTab from "@/pages/admin/LocalGroupsTab";
import ReportsTab from "@/pages/admin/ReportsTab";
import EmailConfigTab from "@/pages/admin/EmailConfigTab";

const TABS = [
  { id: "stats", l: "📊 Tableau de bord" },
  { id: "articles", l: "📰 Articles (Actualités)" },
  { id: "consultations", l: "🗳️ Sondages & consultations (Donner mon avis)" },
  { id: "contenus", l: "📚 Articles pédago (Comprendre) & Pack Action" },
  { id: "groupes", l: "📍 Groupes locaux (Carte)" },
  { id: "pages", l: "📝 Textes & blocs de page" },
  { id: "faq", l: "❓ FAQ (Connaître tes droits)" },
  { id: "moderation", l: "🛡️ Modération témoignages" },
  { id: "reports", l: "🚨 Signalements" },
  { id: "membres", l: "👥 Membres inscrit·e·s" },
  { id: "schools", l: "🏫 Annuaire des écoles" },
  { id: "team", l: "🔑 Équipe admin" },
  { id: "site", l: "🏠 Page d'accueil & config site" },
  { id: "emails", l: "✉️ Emails (Brevo)" },
  { id: "files", l: "🗂️ Médiathèque (images & fichiers)" },
];

const CONS_STATUS_OPTIONS = [
  { value: "brouillon", label: "📝 Brouillon (non public)" },
  { value: "en_cours", label: "🟢 En cours" },
  { value: "termine", label: "✅ Terminée" },
  { value: "archivee", label: "📦 Archivée" },
];

const CONS_STATUS_COLOR = {
  brouillon: "bg-neutral-300",
  en_cours: "bg-cef-green",
  termine: "bg-cef-blue text-white",
  archivee: "bg-neutral-200 text-neutral-500",
};

const ART_CATEGORIES_DEFAULT = ["Réformes de l'enseignement", "Mobilisations", "Vie scolaire", "Bien-être", "Numérique", "Harcèlement", "Orientation", "Participation des élèves"];

const CONS_TYPES = ["sondage", "questionnaire", "vote", "boite_idees", "temoignage"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = React.useState("stats");
  const [articles, setArticles] = React.useState([]);
  const [consults, setConsults] = React.useState([]);
  const [joins, setJoins] = React.useState([]);
  const [pending, setPending] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [files, setFiles] = React.useState([]);
  const [badges, setBadges] = React.useState([]);
  const [showArt, setShowArt] = React.useState(false);
  const [showCons, setShowCons] = React.useState(false);
  const [editing, setEditing] = React.useState(null); // {type, item}
  const [art, setArt] = React.useState({ slug: "", title: "", category: "Vie scolaire", excerpt: "", content: "", image_url: "", tags: "", featured: false });
  const [cons, setCons] = React.useState({ slug: "", title: "", description: "", type: "sondage", options: "", status: "en_cours", follow_up: "" });
  const [uploading, setUploading] = React.useState(false);
  const [showArchivedArt, setShowArchivedArt] = React.useState(false);
  const [showArchivedCons, setShowArchivedCons] = React.useState(false);
  const [artCategories, setArtCategories] = React.useState(ART_CATEGORIES_DEFAULT);

  const load = React.useCallback(() => {
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/admin/consultations").then((r) => setConsults(r.data)).catch(() => {});
    api.get("/admin/join-requests").then((r) => setJoins(r.data)).catch(() => {});
    api.get("/admin/testimonials/pending").then((r) => setPending(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/files").then((r) => setFiles(r.data)).catch(() => {});
    api.get("/badges").then((r) => setBadges(r.data));
    api.get("/site-config").then((r) => {
      const cats = r.data?.categories?.articles;
      if (Array.isArray(cats) && cats.length > 0) setArtCategories(cats);
    }).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const logout = () => { clearToken(); nav("/admin/login"); };

  // Memoized filtered lists (perf — avoid recomputing on every render)
  const filteredArticles = React.useMemo(
    () => articles.filter((a) => showArchivedArt || !a.archived),
    [articles, showArchivedArt]
  );
  const filteredConsults = React.useMemo(
    () => consults.filter((c) => showArchivedCons || c.status !== "archivee"),
    [consults, showArchivedCons]
  );

  const submitArt = async (e) => {
    e.preventDefault();
    try {
      await api.post("/articles", { ...art, tags: art.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Article publié");
      setShowArt(false);
      setArt({ slug: "", title: "", category: "Vie scolaire", excerpt: "", content: "", image_url: "", tags: "", featured: false });
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); }
  };

  const submitCons = async (e) => {
    e.preventDefault();
    try {
      await api.post("/consultations", { ...cons, options: cons.options.split(",").map((o) => o.trim()).filter(Boolean), follow_up: cons.follow_up || null });
      toast.success("Consultation créée");
      setShowCons(false);
      setCons({ slug: "", title: "", description: "", type: "sondage", options: "", status: "en_cours", follow_up: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); }
  };

  const delArt = async (id) => {
    if (!window.confirm("Supprimer DÉFINITIVEMENT cet article ? (Préfère 'Archiver' pour le masquer sans le perdre)")) return;
    await api.delete(`/articles/${id}`);
    toast.success("Supprimé"); load();
  };

  const toggleArtArchive = async (a) => {
    try {
      await api.patch(`/admin/articles/${a.id}`, { archived: !a.archived });
      toast.success(a.archived ? "Article restauré" : "Article archivé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); }
  };

  const setConsultationStatus = async (c, status) => {
    try {
      await api.patch(`/admin/consultations/${c.id}`, { status });
      toast.success(`Statut → ${status}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); }
  };

  const delConsultation = async (id) => {
    if (!window.confirm("Supprimer DÉFINITIVEMENT cette consultation ? (Préfère 'Archivée' pour la masquer)")) return;
    try {
      await api.delete(`/admin/consultations/${id}`);
      toast.success("Supprimée"); load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); }
  };

  const saveArticleEdit = async (vals) => {
    try {
      const payload = { ...vals };
      if (typeof payload.tags === "string") payload.tags = payload.tags.split(",").map((t) => t.trim()).filter(Boolean);
      await api.patch(`/admin/articles/${editing.item.id}`, payload);
      toast.success("Article mis à jour");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); throw err; }
  };

  const saveConsultationEdit = async (vals) => {
    try {
      const payload = { ...vals };
      if (typeof payload.options === "string") payload.options = payload.options.split(",").map((o) => o.trim()).filter(Boolean);
      if (payload.respondents_override === "" || payload.respondents_override === undefined) payload.respondents_override = null;
      if (payload.external_url === "") payload.external_url = null;
      await api.patch(`/admin/consultations/${editing.item.id}`, payload);
      toast.success("Consultation mise à jour");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); throw err; }
  };

  const approveT = async (id) => {
    await api.post(`/admin/testimonials/${id}/approve`);
    toast.success("Témoignage approuvé");
    load();
  };
  const rejectT = async (id) => {
    if (!window.confirm("Rejeter ce témoignage ?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Rejeté");
    load();
  };

  const grantBadge = async (userId, slug) => {
    try {
      await api.post(`/admin/users/${userId}/badges`, { slug });
      toast.success("Badge attribué");
      load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Erreur"); }
  };
  const revokeBadge = async (userId, slug) => {
    await api.delete(`/admin/users/${userId}/badges/${slug}`);
    toast.success("Badge retiré");
    load();
  };

  const downloadCsv = async (endpoint, filename) => {
    try {
      const { data } = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} téléchargé`);
    } catch (err) {
      toast.error("Erreur de téléchargement");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploadé : ${data.original_filename}`);
      load();
    } catch (err) {
      toast.error("Upload échoué");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const copyUrl = (path) => {
    const url = `${API_BASE}/files/${path}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copiée !");
  };

  return (
    <div className="min-h-screen bg-cef-paper">
      <header className="bg-white border-b-2 border-black sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-black tracking-tighter text-xl flex items-center gap-2">
            <CefLogoImg className="w-8 h-8" /> ÉLÈVE TA VOIX<span className="text-cef-pink">.</span> ADMIN
          </Link>
          <button onClick={logout} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Déconnexion</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`chip ${tab === t.id ? "bg-black text-white" : "bg-white"}`} data-testid={`admin-tab-${t.id}`}>{t.l}</button>
          ))}
        </div>

        {tab === "stats" && <AdminStats />}

        {tab === "articles" && (
          <div>
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
              <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Articles ({filteredArticles.length}{showArchivedArt ? " inclus archivés" : ""})</h2>
              <div className="flex gap-2">
                <label className="chip bg-white inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={showArchivedArt} onChange={(e) => setShowArchivedArt(e.target.checked)} data-testid="toggle-archived-articles" /> Voir archivés
                </label>
                <button onClick={() => setShowArt((s) => !s)} className="neo-btn bg-cef-blue text-white" data-testid="new-article-btn"><Plus className="w-4 h-4" /> Nouveau</button>
              </div>
            </div>
            {showArt && (
              <form onSubmit={submitArt} className="neo-card p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="new-article-form">
                <input className="neo-input" placeholder="Slug (ex: ma-belle-actu)" value={art.slug} onChange={(e) => setArt({ ...art, slug: e.target.value })} required />
                <input className="neo-input" placeholder="Titre" value={art.title} onChange={(e) => setArt({ ...art, title: e.target.value })} required />
                <select className="neo-input" value={art.category} onChange={(e) => setArt({ ...art, category: e.target.value })}>
                  {artCategories.map((c) => <option key={c}>{c}</option>)}
                </select>
                <div className="sm:col-span-2">
                  <ImageUpload value={art.image_url} onChange={(url) => setArt({ ...art, image_url: url })} label="Image de couverture" testId="new-article-image" />
                </div>
                <input className="neo-input sm:col-span-2" placeholder="Résumé" value={art.excerpt} onChange={(e) => setArt({ ...art, excerpt: e.target.value })} required />
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-tight mb-1 block">Contenu — mise en forme (gras, italique, listes, liens…)</label>
                  <RichTextEditor value={art.content} onChange={(html) => setArt({ ...art, content: html })} testId="new-article-content" />
                </div>
                <input className="neo-input" placeholder="Tags (séparés par ,)" value={art.tags} onChange={(e) => setArt({ ...art, tags: e.target.value })} />
                <label className="flex items-center gap-2"><input type="checkbox" checked={art.featured} onChange={(e) => setArt({ ...art, featured: e.target.checked })} /> À la une</label>
                <button className="neo-btn bg-cef-pink text-white sm:col-span-2" type="submit">Publier</button>
              </form>
            )}
            <div className="grid gap-3">
              {filteredArticles.map((a) => (
                <div key={a.id} className={`neo-card p-4 flex items-center justify-between gap-3 flex-wrap ${a.archived ? "opacity-60" : ""}`} data-testid={`admin-article-${a.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-bold flex items-center gap-2">
                      {a.archived && <span className="chip bg-neutral-300 text-xs">📦 archivé</span>}
                      {a.featured && <span className="chip bg-cef-yellow text-xs">⭐ à la une</span>}
                      {a.title}
                    </div>
                    <div className="text-xs text-neutral-500">{a.category} · {new Date(a.published_at).toLocaleDateString("fr-BE")}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ type: "article", item: { ...a, tags: (a.tags || []).join(", ") } })} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`edit-article-${a.id}`}><Edit3 className="w-4 h-4" /> Éditer</button>
                    <button onClick={() => toggleArtArchive(a)} className={`neo-btn text-sm py-2 ${a.archived ? "bg-cef-green" : "bg-white"}`} data-testid={`archive-article-${a.id}`}>
                      {a.archived ? <><ArchiveRestore className="w-4 h-4" /> Restaurer</> : <><Archive className="w-4 h-4" /> Archiver</>}
                    </button>
                    <button onClick={() => delArt(a.id)} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid={`del-article-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "consultations" && (
          <div>
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
              <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Consultations ({filteredConsults.length})</h2>
              <div className="flex gap-2">
                <label className="chip bg-white inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={showArchivedCons} onChange={(e) => setShowArchivedCons(e.target.checked)} data-testid="toggle-archived-cons" /> Voir archivées
                </label>
                <button onClick={() => setShowCons((s) => !s)} className="neo-btn bg-cef-blue text-white" data-testid="new-consult-btn"><Plus className="w-4 h-4" /> Nouvelle</button>
              </div>
            </div>
            {showCons && (
              <form onSubmit={submitCons} className="neo-card p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="new-consult-form">
                <input className="neo-input" placeholder="Slug" value={cons.slug} onChange={(e) => setCons({ ...cons, slug: e.target.value })} required />
                <input className="neo-input" placeholder="Titre" value={cons.title} onChange={(e) => setCons({ ...cons, title: e.target.value })} required />
                <select className="neo-input" value={cons.type} onChange={(e) => setCons({ ...cons, type: e.target.value })}>
                  {CONS_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <select className="neo-input" value={cons.status} onChange={(e) => setCons({ ...cons, status: e.target.value })}>
                  {CONS_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <textarea className="neo-input sm:col-span-2 min-h-[80px]" placeholder="Description" value={cons.description} onChange={(e) => setCons({ ...cons, description: e.target.value })} required />
                <input className="neo-input sm:col-span-2" placeholder="Options (séparées par ,)" value={cons.options} onChange={(e) => setCons({ ...cons, options: e.target.value })} />
                <input className="neo-input sm:col-span-2" placeholder="Suite donnée (optionnel)" value={cons.follow_up} onChange={(e) => setCons({ ...cons, follow_up: e.target.value })} />
                <button className="neo-btn bg-cef-pink text-white sm:col-span-2" type="submit">Créer</button>
              </form>
            )}
            <div className="grid gap-3">
              {filteredConsults.map((c) => (
                <div key={c.id} className={`neo-card p-4 ${c.status === "archivee" || c.status === "brouillon" ? "opacity-70" : ""}`} data-testid={`admin-cons-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-bold">{c.title}</div>
                      <div className="text-xs text-neutral-500 mt-1">{c.type} · {c.participants_count || 0} participants · slug: <code>{c.slug}</code></div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <select value={c.status} onChange={(e) => setConsultationStatus(c, e.target.value)} className={`neo-input text-xs py-1.5 ${CONS_STATUS_COLOR[c.status] || ""}`} data-testid={`cons-status-${c.id}`}>
                        {CONS_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button onClick={() => setEditing({ type: "consultation", item: { ...c, options: (c.options || []).join(", ") } })} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`edit-cons-${c.id}`}><Edit3 className="w-4 h-4" /> Éditer</button>
                      <button onClick={() => delConsultation(c.id)} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid={`del-cons-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "contenus" && <ContenusTab />}
        {tab === "groupes" && <LocalGroupsTab />}
        {tab === "reports" && <ReportsTab />}
        {tab === "emails" && <EmailConfigTab />}
        {tab === "pages" && <PageBlocksTab />}

        {tab === "moderation" && (
          <div>
            <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Témoignages à modérer ({pending.length})</h2>
            <div className="grid gap-3" data-testid="moderation-list">
              {pending.length === 0 && <div className="neo-card p-5 text-neutral-500">Aucun témoignage en attente. 👌</div>}
              {pending.map((t) => (
                <div key={t.id} className="neo-card p-5" data-testid={`pending-${t.id}`}>
                  <p className="text-base leading-relaxed">« {t.text} »</p>
                  <div className="mt-2 text-xs text-neutral-500">{t.author} {t.age ? `· ${t.age} ans` : ""} {t.school ? `· ${t.school}` : ""} {t.theme ? `· #${t.theme}` : ""}</div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => approveT(t.id)} className="neo-btn bg-cef-green text-sm py-2" data-testid={`approve-${t.id}`}><Check className="w-4 h-4" /> Approuver</button>
                    <button onClick={() => rejectT(t.id)} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid={`reject-${t.id}`}><Trash2 className="w-4 h-4" /> Rejeter</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "membres" && <MembresTab />}

        {tab === "schools" && <SchoolsTab />}

        {tab === "team" && <TeamTab />}
        {tab === "site" && <SiteConfigTab />}
        {tab === "faq" && <FAQAdminTab />}

        {tab === "files" && (
          <FilesTab files={files} uploading={uploading} handleUpload={handleUpload} copyUrl={copyUrl} />
        )}

        {tab === "joins" && null /* legacy — replaced by 'Membres' */}
      </div>

      {editing?.type === "article" && (
        <EditModal
          title="Éditer l'article"
          onClose={() => setEditing(null)}
          onSave={saveArticleEdit}
          initial={editing.item}
          fields={[
            { key: "slug", label: "Slug", required: true },
            { key: "title", label: "Titre", required: true },
            { key: "category", label: "Catégorie", type: "select", options: artCategories },
            { key: "image_url", label: "Image", type: "image" },
            { key: "excerpt", label: "Résumé", multiline: true, span: 2, required: true },
            { key: "content", label: "Contenu (mise en forme : gras, italique, listes, liens…)", type: "richtext", required: true },
            { key: "tags", label: "Tags (séparés par ,)", hint: "Exemple: école, droits, réformes" },
            { key: "author", label: "Auteur·rice" },
            { key: "featured", label: "À la une", type: "checkbox" },
            { key: "archived", label: "Archivé (masqué du site)", type: "checkbox" },
          ]}
        />
      )}

      {editing?.type === "consultation" && (
        <EditModal
          title="Éditer la consultation"
          onClose={() => setEditing(null)}
          onSave={saveConsultationEdit}
          initial={editing.item}
          fields={[
            { key: "slug", label: "Slug", required: true },
            { key: "title", label: "Titre", required: true },
            { key: "type", label: "Type", type: "select", options: CONS_TYPES, required: true },
            { key: "status", label: "Statut", type: "select", options: CONS_STATUS_OPTIONS, required: true },
            { key: "description", label: "Description", multiline: true, span: 2, required: true },
            { key: "options", label: "Options (séparées par ,)", span: 2 },
            { key: "external_url", label: "URL externe (Google Form, Typeform…) — laisse vide pour utiliser le formulaire intégré", span: 2, hint: "Si renseigné, le bouton ouvrira ce lien dans un nouvel onglet." },
            { key: "respondents_override", label: "Compteur manuel de répondant·e·s (sondages externes)", type: "number", hint: "Laisse vide pour utiliser le compteur automatique." },
            { key: "follow_up", label: "Suite donnée", multiline: true, span: 2 },
            { key: "closes_at", label: "Date de clôture (ISO)" },
          ]}
        />
      )}
    </div>
  );
}

function UserCard({ user, badges, onGrant, onRevoke }) {
  const [open, setOpen] = React.useState(false);
  const manualBadges = badges.filter((b) => b.manual);
  return (
    <div className="neo-card p-4" data-testid={`user-${user.user_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-bold">{user.name} <span className="text-xs text-neutral-500 font-normal">· {user.email}</span></div>
          <div className="text-xs text-neutral-500">{user.school || "—"} · {user.city || "—"} · {user.province || "—"} · {user.provider || "?"}</div>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="neo-btn bg-cef-yellow text-sm py-2" data-testid={`user-badges-toggle-${user.user_id}`}><Award className="w-4 h-4" /> Badges</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {(user.badges || []).map((s) => {
          const b = badges.find((x) => x.slug === s);
          return <span key={s} className={`chip text-xs ${b?.color || "bg-white"}`}>{b?.emoji} {b?.label || s}</span>;
        })}
      </div>
      {open && (
        <div className="mt-3 border-t-2 border-black pt-3">
          <div className="text-xs font-bold uppercase mb-2">Attribuer un badge manuel</div>
          <div className="flex flex-wrap gap-2">
            {manualBadges.map((b) => (
              <button key={b.slug} onClick={() => (user.badges?.includes(b.slug) ? onRevoke(user.user_id, b.slug) : onGrant(user.user_id, b.slug))} className={`chip ${user.badges?.includes(b.slug) ? b.color : "bg-white"}`} data-testid={`grant-${b.slug}-${user.user_id}`}>
                {b.emoji} {b.label} {user.badges?.includes(b.slug) ? "✕" : "+"}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TeamTab() {
  const [admins, setAdmins] = React.useState([]);
  const [show, setShow] = React.useState(false);
  const [form, setForm] = React.useState({ email: "", password: "", name: "" });

  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => {});
  React.useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Mot de passe min. 8 caractères");
    try {
      await api.post("/admin/admins", form);
      toast.success("Admin créé");
      setForm({ email: "", password: "", name: "" });
      setShow(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const del = async (email) => {
    if (!window.confirm(`Retirer ${email} de l'équipe ?`)) return;
    try {
      await api.delete(`/admin/admins/${encodeURIComponent(email)}`);
      toast.success("Admin retiré");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Équipe admin ({admins.length})</h2>
        <button onClick={() => setShow((s) => !s)} className="neo-btn bg-cef-blue text-white" data-testid="new-admin-btn"><UserPlus className="w-4 h-4" /> Ajouter un·e admin</button>
      </div>
      {show && (
        <form onSubmit={submit} className="neo-card p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="new-admin-form">
          <input className="neo-input" placeholder="Prénom / nom (optionnel)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-new-name" />
          <input className="neo-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="admin-new-email" />
          <input className="neo-input sm:col-span-2" type="password" placeholder="Mot de passe (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} data-testid="admin-new-password" />
          <button type="submit" className="neo-btn bg-cef-pink text-white sm:col-span-2" data-testid="admin-new-submit">Créer l'accès admin</button>
        </form>
      )}
      <div className="grid gap-3" data-testid="admins-list">
        {admins.map((a) => (
          <div key={a.email} className="neo-card p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`admin-${a.email}`}>
            <div>
              <div className="font-bold">{a.name || a.email} {a.is_me && <span className="chip bg-cef-yellow text-xs ml-2">moi</span>} {a.is_seed && <span className="chip bg-black text-white text-xs ml-2">par défaut</span>}</div>
              <div className="text-xs text-neutral-500">{a.email} · ajouté le {new Date(a.created_at).toLocaleDateString("fr-BE")}</div>
            </div>
            {!a.is_me && !a.is_seed && (
              <button onClick={() => del(a.email)} className="neo-btn bg-white text-sm py-2" data-testid={`admin-delete-${a.email}`}><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SchoolsTab() {
  const [count, setCount] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [lastImport, setLastImport] = React.useState(null);
  const [provincesStats, setProvincesStats] = React.useState([]);

  const load = async () => {
    try {
      const { data: schools } = await api.get("/schools", { params: { limit: 5000 } });
      setCount(schools.length);
      const map = {};
      schools.forEach((s) => { map[s.province] = (map[s.province] || 0) + 1; });
      setProvincesStats(Object.entries(map).sort((a, b) => b[1] - a[1]));
    } catch (err) {
      console.error("Failed to load schools stats:", err);
    }
  };
  React.useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Importer ${file.name} et REMPLACER toute la liste actuelle (${count} écoles) ?`)) {
      e.target.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/schools/upload-csv?replace=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import OK : ${data.inserted} écoles`);
      setLastImport(data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'import");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Écoles ({count ?? "…"})</h2>
        <label className={`neo-btn bg-cef-blue text-white cursor-pointer ${uploading ? "opacity-50" : ""}`} data-testid="schools-upload-label">
          <FileSpreadsheet className="w-4 h-4" /> {uploading ? "Import…" : "Importer un nouveau CSV"}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} disabled={uploading} data-testid="schools-csv-input" />
        </label>
      </div>
      <div className="neo-card p-5 bg-cef-yellow mb-6">
        <div className="font-display font-black text-lg uppercase mb-2">Format attendu</div>
        <p className="text-sm">CSV séparé par <code>;</code>, encodage UTF-8, en-têtes : <code>Nom; Adresse; Téléphone; Site Web; Réseau; Immersion Langue</code>. La province est déduite automatiquement du code postal (4 chiffres en fin d'adresse).</p>
        <p className="text-sm mt-2"><strong>⚠️</strong> L'import remplace toute la liste actuelle. Sauvegarde l'ancien fichier avant d'importer.</p>
      </div>
      {lastImport && (
        <div className="neo-card p-4 mb-4 bg-cef-green" data-testid="last-import">
          ✅ Dernier import : <strong>{lastImport.inserted}</strong> écoles importées depuis {lastImport.filename}.
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="schools-provinces-stats">
        {provincesStats.map(([prov, n]) => (
          <div key={prov} className="neo-card p-4 bg-white">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500">{prov}</div>
            <div className="mt-1 font-display font-black text-2xl">{n}</div>
            <div className="text-xs">écoles</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilesTab({ files, uploading, handleUpload, copyUrl }) {
  const [q, setQ] = React.useState("");
  const [sortKey, setSortKey] = React.useState("date_desc");
  const [type, setType] = React.useState("");

  const filtered = React.useMemo(() => {
    let list = files.filter((f) => {
      if (type === "image" && !(f.content_type || "").startsWith("image/")) return false;
      if (type === "pdf" && !(f.content_type || "").includes("pdf")) return false;
      if (type === "other" && ((f.content_type || "").startsWith("image/") || (f.content_type || "").includes("pdf"))) return false;
      if (q && !(f.original_filename || "").toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
    const cmp = {
      name_asc: (a, b) => (a.original_filename || "").localeCompare(b.original_filename || ""),
      name_desc: (a, b) => (b.original_filename || "").localeCompare(a.original_filename || ""),
      date_asc: (a, b) => (a.uploaded_at || "").localeCompare(b.uploaded_at || ""),
      date_desc: (a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""),
      size_asc: (a, b) => (a.size || 0) - (b.size || 0),
      size_desc: (a, b) => (b.size || 0) - (a.size || 0),
    }[sortKey];
    return [...list].sort(cmp);
  }, [files, q, sortKey, type]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase">Fichiers ({filtered.length}/{files.length})</h2>
        <label className={`neo-btn bg-cef-blue text-white cursor-pointer ${uploading ? "opacity-50" : ""}`} data-testid="upload-label">
          <Upload className="w-4 h-4" /> {uploading ? "Upload…" : "Uploader un fichier"}
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-input" />
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-2 mb-4" data-testid="files-toolbar">
        <input className="neo-input" placeholder="Rechercher un fichier…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="files-search" />
        <select className="neo-input" value={type} onChange={(e) => setType(e.target.value)} data-testid="files-type">
          <option value="">Tous types</option>
          <option value="image">Images</option>
          <option value="pdf">PDF</option>
          <option value="other">Autres</option>
        </select>
        <select className="neo-input" value={sortKey} onChange={(e) => setSortKey(e.target.value)} data-testid="files-sort">
          <option value="date_desc">Trier : Plus récents</option>
          <option value="date_asc">Trier : Plus anciens</option>
          <option value="name_asc">Trier : Nom (A → Z)</option>
          <option value="name_desc">Trier : Nom (Z → A)</option>
          <option value="size_desc">Trier : Plus volumineux</option>
          <option value="size_asc">Trier : Plus légers</option>
        </select>
      </div>
      <div className="grid gap-3" data-testid="files-list">
        {filtered.length === 0 && <div className="neo-card p-5 text-neutral-500">Aucun fichier ne correspond à ces filtres.</div>}
        {filtered.map((f) => (
          <div key={f.id} className="neo-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`file-${f.id}`}>
            <div>
              <div className="font-bold">{f.original_filename}</div>
              <div className="text-xs text-neutral-500">{f.content_type} · {Math.round((f.size || 0) / 1024)} Ko {f.uploaded_at && `· ${new Date(f.uploaded_at).toLocaleDateString("fr-BE")}`}</div>
            </div>
            <div className="flex gap-2">
              <a href={`${API_BASE}/files/${f.storage_path}`} target="_blank" rel="noreferrer" className="neo-btn bg-white text-sm py-2">Aperçu</a>
              <button onClick={() => copyUrl(f.storage_path)} className="neo-btn bg-cef-yellow text-sm py-2"><Copy className="w-4 h-4" /> URL</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


